/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faHandshake, 
  faComments,
  faArrowLeft,
  faHandsHelping,
  faHeart,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import { formatEmployerName } from '../utils/nameFormatter';
import CustomTooltip from '../components/CustomTooltip';
import '../styles/Companies.css';
import '../styles/Communities.css';

const NGOs = () => {
  const navigate = useNavigate();
  const [ngos, setNgos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadNGOs = async () => {
      try {
        setLoading(true);
        // Use dedicated NGO endpoint instead of companies endpoint
        const response = await axios.get(buildApiUrl('/api/ngo/list'));
        // Handle both 'ngos' and 'companies' fields in response for compatibility
        const allCompanies = response.data?.ngos || response.data?.companies || [];
        
        console.log(`📋 Total NGOs from dedicated API endpoint: ${allCompanies.length}`);
        
        // Define strict allowed NGO names - ONLY these 3 specific NGOs
        // We'll match exactly these names and their variations
        
        // Filter to ONLY show ELAFA - Baps Africa and RocketSuites removed
        const allowed = allCompanies.filter(company => {
          const companyName = (company.companyName || company.ngoName || '').toLowerCase().trim();
          
          // Check for ELAFA - must contain "elite" AND ("ability" OR "elafa")
          const isElafa = companyName.includes('elite') && (companyName.includes('ability') || companyName.includes('elafa'));
          
          const isAllowed = isElafa;
          
          // Log what's being filtered
          if (!isAllowed && companyName) {
            console.log(`🚫 Filtering out NGO: "${company.companyName || company.ngoName}" (does not match allowed list)`);
          } else if (isAllowed) {
            console.log(`✅ Allowing NGO: "${company.companyName || company.ngoName}" (matched: ELAFA)`);
          }
          
          return isAllowed;
        });
        
        console.log(`✅ Filtered to allowed NGOs: ${allowed.length}`);
        console.log(`📋 Allowed NGO names:`, allowed.map(n => n.companyName || n.ngoName));
        
        // Remove duplicates based on _id
        const seenIds = new Set();
        const seenNames = new Set(); // Also track by name to catch duplicates with different IDs
        
        const uniqueNGOs = allowed.filter(ngo => {
          const id = ngo._id || ngo.id;
          const name = (ngo.companyName || ngo.ngoName || '').toLowerCase().trim();
          
          // Skip if we've seen this ID before
          if (id && seenIds.has(id)) {
            console.log(`⚠️ Removing duplicate NGO by ID: ${ngo.companyName || ngo.ngoName} (ID: ${id})`);
            return false;
          }
          
          // Skip if we've seen this name before (to catch duplicates with different IDs)
          if (name && seenNames.has(name)) {
            console.log(`⚠️ Removing duplicate NGO by name: ${ngo.companyName || ngo.ngoName} (Name already seen)`);
            return false;
          }
          
          if (id) seenIds.add(id);
          if (name) seenNames.add(name);
          return true;
        });
        
        console.log(`✅ Unique NGOs after deduplication: ${uniqueNGOs.length}`);
        console.log(`📋 Final NGO list:`, uniqueNGOs.map(n => ({ name: n.companyName || n.ngoName, id: n._id || n.id })));
        
        // Final filter: only exclude if showLogoOnWebsite is explicitly false
        // We've already filtered to only the 3 allowed NGOs above
        const filtered = uniqueNGOs.filter(ngo => {
          // Exclude only if showLogoOnWebsite is explicitly false
          if (ngo.showLogoOnWebsite === false) {
            console.log(`🚫 Excluding NGO "${ngo.companyName || ngo.ngoName}" because showLogoOnWebsite is false`);
            return false;
          }
          return true;
        });
        
        console.log(`✅ Final filtered NGOs: ${filtered.length}`);
        console.log(`📋 Final NGO names:`, filtered.map(n => ({
          name: n.companyName || n.ngoName,
          hasLogo: !!(n.ngoLogo || n.companyLogo),
          logoPath: n.ngoLogo || n.companyLogo,
          showLogo: n.showLogoOnWebsite
        })));
        
        // Verify we only have ELAFA
        const finalNames = filtered.map(n => (n.companyName || n.ngoName || '').toLowerCase());
        const hasElafa = finalNames.some(n => n.includes('elite') || n.includes('elafa'));
        
        console.log(`✅ Verification: ELAFA=${hasElafa}`);
        
        setNgos(filtered);
      } catch (error) {
        console.error('❌ Error fetching NGOs:', error);
        setNgos([]);
      } finally {
        setLoading(false);
      }
    };

    loadNGOs();
  }, []);

  const stats = useMemo(() => {
    const totalPartners = ngos.length;
    const totalDiscussions = ngos.reduce((count, ngo) => ngo.focusAreas ? count + 1 : count, 0);
    const totalCollaborations = ngos.reduce((count, ngo) => ngo.memberCount ? count + 1 : count, 0);
    
    return {
      partners: totalPartners,
      discussions: totalDiscussions,
      collaborations: totalCollaborations
    };
  }, [ngos]);

  const getInitials = (name) => {
    if (!name) return 'N';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getDisplayName = (name) => {
    if (!name) return 'NGO';
    // Convert "Elite Ability Foundation Africa" to "ELAFA"
    if (name.toLowerCase().includes('elite ability foundation africa') || 
        name.toLowerCase().includes('elite ability foundation')) {
      return 'ELAFA';
    }
    return name;
  };

  return (
    <div className="companies-page communities-page">
      <button 
        className="back-to-home-btn" 
        onClick={() => navigate('/')}
        aria-label="Back to Homepage"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <span>Back to Home</span>
      </button>

      <div className="companies-hero">
        <div className="companies-hero-overlay"></div>
        <div className="companies-hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faHandsHelping} className="badge-icon" />
            <span>NGO NETWORK</span>
          </div>
          <h1 className="companies-hero-title">
            Explore Non-Governmental Organizations <br />
            <span className="gradient-text">Driving Social Impact Together</span>
          </h1>
          <p className="companies-hero-subtitle">
            Join dynamic NGOs and organizations actively engaging to create meaningful opportunities and drive positive social change
          </p>
          <div className="companies-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.partners}+</div>
                <div className="stat-label">Partner NGOs</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faComments} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.discussions}+</div>
                <div className="stat-label">Active Discussions</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faHandshake} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.collaborations}+</div>
                <div className="stat-label">Collaborations</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      {/* NGO Partners Section */}
      <section className="companies-container" style={{ paddingTop: '3rem', paddingBottom: '2rem' }}>
        <div style={{ marginBottom: '2rem', maxWidth: '760px', margin: '0 auto 2rem auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem', textAlign: 'center' }}>
            Our NGO Partners
          </h2>
          <p style={{ color: '#4b5563', fontSize: '16px', margin: 0, textAlign: 'center' }}>
            Non-Governmental Organizations partnering with Akshar Jobs to drive social impact and create meaningful opportunities.
          </p>
        </div>

        {loading ? (
          <div className="companies-loading">
            <div className="loading-spinner"></div>
            <p>Loading NGOs...</p>
          </div>
        ) : (
          <div className="companies-grid">
            {ngos.map((ngo, index) => {
              const originalName = ngo.companyName || ngo.ngoName || ngo.employerName || 'NGO';
              const name = getDisplayName(formatEmployerName(originalName));
              
              // Check if logo should be shown (showLogoOnWebsite must not be false)
              const showLogo = ngo.showLogoOnWebsite !== false;
              let logo = '';
              let originalLogoPath = '';
              if (showLogo) {
                // Check ngoLogo first, then companyLogo, then logo (for backwards compatibility)
                originalLogoPath = ngo.ngoLogo || ngo.companyLogo || ngo.logo || '';
                logo = originalLogoPath;
                // If logo is a relative path, construct full URL
                if (logo && !logo.startsWith('http://') && !logo.startsWith('https://')) {
                  const normalized = logo.replace(/\\/g, '/').trim();
                  
                  // Remove leading slash if present
                  let pathPart = normalized;
                  if (pathPart.startsWith('/uploads/')) {
                    pathPart = pathPart.substring('/uploads/'.length);
                  } else if (pathPart.startsWith('uploads/')) {
                    pathPart = pathPart.substring('uploads/'.length);
                  }
                  
                  // Construct the API URL
                  if (pathPart) {
                    logo = buildApiUrl(`/api/users/uploads/${pathPart}`);
                    // Debug logging - enhanced for Rocketsuites
                    const isRocketsuites = name.toLowerCase().includes('rocket');
                    if (isRocketsuites) {
                      console.log(`🚀🚀🚀 ROCKETSUITES Logo URL Construction:`, {
                        name: name,
                        originalPath: originalLogoPath,
                        normalized: normalized,
                        pathPart: pathPart,
                        finalUrl: logo,
                        showLogo: showLogo,
                        buildApiUrl_base: buildApiUrl(''),
                        fullPath: `/api/users/uploads/${pathPart}`
                      });
                    } else {
                      console.log(`🔍 NGO Logo URL constructed for "${name}":`, {
                        originalPath: originalLogoPath,
                        normalized: normalized,
                        pathPart: pathPart,
                        finalUrl: logo,
                        showLogo: showLogo
                      });
                    }
                  } else {
                    console.warn(`⚠️ Empty pathPart for "${name}", originalLogoPath: ${originalLogoPath}`);
                  }
                }
              }

              // Use a unique key combining _id and index to avoid duplicates
              const uniqueKey = `${ngo._id || ngo.id || 'unknown'}-${index}`;
              
              return (
                <CustomTooltip text={originalName} position="top" key={uniqueKey}>
                  <div 
                    className="company-card" 
                    style={{ cursor: 'pointer' }}
                  >
                  <div className="company-card-header">
                    <div className="card-header-bg"></div>
                    <div className="company-logo-wrapper">
                      {logo && showLogo ? (
                        <img
                          src={logo}
                          alt={name}
                          className="company-logo"
                          onError={(e) => {
                            console.error(`❌ Failed to load NGO logo for "${name}":`, {
                              logoUrl: logo,
                              originalPath: originalLogoPath,
                              error: 'Image load failed'
                            });
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextSibling;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                          onLoad={() => {
                            console.log(`✅ Successfully loaded NGO logo for "${name}":`, logo);
                          }}
                        />
                      ) : null}
                      <div 
                        className="company-logo-placeholder"
                        style={{ display: (logo && showLogo) ? 'none' : 'flex' }}
                      >
                        <span className="initials-text">{getInitials(name)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="company-card-body" style={{ padding: '1rem' }}>
                    <h3 className="company-name" style={{ textAlign: 'center', margin: 0 }}>{name}</h3>
                  </div>
                  </div>
                </CustomTooltip>
              );
            })}
          </div>
        )}
      </section>

      {/* NGO Partnership Section */}
      <section className="companies-container" style={{ paddingTop: '3rem', paddingBottom: '2rem' }}>
        <div style={{ 
          background: 'linear-gradient(135deg, #fff5f0 0%, #e0f7fa 100%)', 
          borderRadius: '16px', 
          padding: '3rem 2rem',
          marginBottom: '3rem',
          border: '2px solid rgba(255, 140, 66, 0.1)'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.75rem',
              background: 'linear-gradient(135deg, #ff6b35, #f97316)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '30px',
              marginBottom: '1.5rem',
              fontSize: '0.875rem',
              fontWeight: '700',
              boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
            }}>
              <FontAwesomeIcon icon={faHandsHelping} />
              <span>NGO PARTNERSHIP</span>
            </div>
            <h2 style={{ 
              fontSize: '32px', 
              fontWeight: 800, 
              color: '#1f2937', 
              marginBottom: '1rem',
              lineHeight: '1.2'
            }}>
              NGOs: Driving Social Impact Together
            </h2>
            <p style={{ 
              color: '#4b5563', 
              fontSize: '18px', 
              marginBottom: '2rem',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '0 auto 2rem auto'
            }}>
              Non-Governmental Organizations play a crucial role in our community network. Join us to connect with passionate volunteers, create meaningful opportunities, and drive positive social change.
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '1.5rem',
              marginTop: '2.5rem'
            }}>
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(255, 107, 53, 0.1)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ff6b35, #f97316)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: 'white',
                  fontSize: '24px'
                }}>
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem' }}>
                  Connect Volunteers
                </h3>
                <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                  Access a network of dedicated volunteers and professionals ready to support your mission and initiatives.
                </p>
              </div>

              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(20, 184, 166, 0.1)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: 'white',
                  fontSize: '24px'
                }}>
                  <FontAwesomeIcon icon={faRocket} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem' }}>
                  Post Opportunities
                </h3>
                <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                  Share volunteer opportunities, project-based roles, and social impact initiatives with our community.
                </p>
              </div>

              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(255, 107, 53, 0.1)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ff6b35, #f97316)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: 'white',
                  fontSize: '24px'
                }}>
                  <FontAwesomeIcon icon={faHeart} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem' }}>
                  Build Partnerships
                </h3>
                <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                  Collaborate with other organizations, track impact metrics, and expand your network of supporters.
                </p>
              </div>
            </div>

            <div style={{ marginTop: '2.5rem' }}>
              <Link 
                to="/signup" 
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'linear-gradient(135deg, #ff6b35, #f97316)',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '16px',
                  boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 107, 53, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.3)';
                }}
              >
                <FontAwesomeIcon icon={faRocket} />
                Join as an NGO Partner
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NGOs;
